import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import api from '../../services/api';
import { toast } from 'sonner';
import { KeyRound, Loader2, Search } from 'lucide-react';

const ACTION_LABELS = {
  self_change: { label: 'Self Change', cls: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  admin_reset: { label: 'Admin Reset', cls: 'bg-amber-500/10 text-amber-700 border-amber-500/20' },
  admin_otp: { label: 'Admin OTP', cls: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20' },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export default function PasswordHistoryPage() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/password-history?limit=500');
        setHistory(res.data?.history || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load password history');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = history.filter((h) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (h.user_email || '').toLowerCase().includes(q) ||
      (h.user_name || '').toLowerCase().includes(q) ||
      (h.actor_email || '').toLowerCase().includes(q) ||
      (h.user_role || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6" data-testid="password-history-page">
      <div>
        <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
          <KeyRound className="h-7 w-7 text-primary" />
          Password Change History
        </h1>
        <p className="text-muted-foreground mt-1">
          Audit log of every password change in your school — self-changes by users
          and admin-initiated resets.
        </p>
      </div>

      <Card className="border border-border">
        <CardHeader>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              {filtered.length} {filtered.length === 1 ? 'entry' : 'entries'}
            </CardTitle>
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter by name, email, role…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="password-history-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <KeyRound className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No password changes recorded yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Changed By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((h) => {
                    const action = ACTION_LABELS[h.action] || { label: h.action || 'Change', cls: 'bg-muted text-muted-foreground' };
                    const selfChange = h.action === 'self_change' || h.user_id === h.actor_user_id;
                    return (
                      <TableRow key={h.id} data-testid={`password-history-row-${h.id}`} className="table-row-hover">
                        <TableCell className="font-mono text-xs">{formatDate(h.changed_at)}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{h.user_name || '-'}</p>
                            <p className="text-xs text-muted-foreground">{h.user_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{h.user_role || '-'}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={action.cls}>{action.label}</Badge>
                        </TableCell>
                        <TableCell>
                          {selfChange ? (
                            <span className="text-xs text-muted-foreground italic">themselves</span>
                          ) : (
                            <div>
                              <p className="text-sm">{h.actor_email || '-'}</p>
                              <p className="text-xs text-muted-foreground capitalize">{h.actor_role}</p>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
